import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from "react-router";
import { removeUserSession } from '../utils/common';
import '../css/Navbar.css';
import axios from 'axios';
import { ChevronLeft, ChevronRight, LogOut, Receipt, ShieldHalf, UserCheck, UsersRound, GraduationCap, MailOpen, Gavel, ClipboardCheck, BookOpen, Shirt, Calculator, HeartPulse, Crosshair, ArrowLeftRight, BookOpen as BookIcon, Car, Wrench, House, Building2, User, LifeBuoy, Truck } from 'lucide-react';
import { NewContext } from '../utils/ContextProvider';
import { NavLink } from 'react-router-dom';
import { getPendingMe } from "../utils/peticionesApi";
import { permisosTecnica, rutaInicialTecnica } from '../pages/admin/tecnica/tecnicaPermisos';

const ROLE_LABELS = {
  ROLE_ADMIN: "Administrador",
  ROLE_RRHH: "RRHH",
  ROLE_JEFE_AREA: "Jefe de Área",
  ROLE_SUPERVISOR: "Supervisor",
  ROLE_REFERENTE: "Supervisor",
  ROLE_OPERADOR: "Operador",
  ROLE_USER: "Empleado",
  ROLE_TECNICO: "Técnico",
};

function Navbar() {
  const { flag, setFlag, getToken, getRol, getDisplayRol, getId, setStates, getModules, isSessionReady, profileImage, selectedEmployee, pendingSummary, isFleetAuthorized, hasAssignedVehicle, isArmamentAuthorized, isDeliveryAuthorized, isUniformAuthorized, isHabAuthorized, isMedLabAuthorized, isCalidadAuthorized, isCapAuthorized, isTecnicaAuthorized, tecnicaScope, tecnicaStockNivel, getCompany, hasAuditorChecklist } = useContext(NewContext);

  const employeeUuid = useMemo(
    () => (typeof getId === 'function' ? getId() : getId),
    [getId]
  );
  const role = useMemo(
    () => (typeof getRol === 'function' ? getRol() : getRol),
    [getRol]
  );
  const modules = useMemo(
    () => (typeof getModules === 'function' ? getModules() : getModules) || [],
    [getModules]
  );

  const hasRole = (...roles) => roles.includes(role);
  const isUser = role === 'ROLE_USER';
  const isTecnico = role === 'ROLE_TECNICO';
  const isBasicRole = isUser || isTecnico;

  // Empresas cuyo item "Sueldos" entra directo al panel de recibos (/haberes).
  // El portal standalone (/portal-sueldos) sigue existiendo con su propio login,
  // pero no se entra desde este menu: usa otro token de sesion y su ruta no pasa
  // por TotpGate, asi que los endpoints admin devuelven 403 y el interceptor recarga.
  const RECIBOS_DIRECTO_COMPANIES = new Set(['testing']);
  const company = useMemo(() => {
    const value = typeof getCompany === 'function' ? getCompany() : getCompany;
    return (value ?? sessionStorage.getItem('company') ?? '').toLowerCase();
  }, [getCompany]);
  const sueldosPath = RECIBOS_DIRECTO_COMPANIES.has(company)
    ? '/haberes'
    : '/sueldos';

  const ALLOWED_MODULES = new Set(['CAPACITACIONES', 'SUELDOS', 'HELPDESK', 'SUPERVISION', 'RONDAS', 'FLOTAS', 'ARMAMENTOS', 'UNIFORMES', 'SIMULACROS', 'EVALUACIONES', 'FACTURACION', 'HABILITACIONES', 'AUDITORIA', 'ANALISIS_RIESGO', 'MEDICINA', 'CALIDAD', 'PRESENTISMO', 'TECNICA', 'EMPRESAS', 'ACCESOS']);
  const hasModule = (m) => {
    return Array.isArray(modules) && ALLOWED_MODULES.has(m) && modules.includes(m);
  };
  // Técnica se muestra con la misma regla que su menú y su guardia (tecnicaPermisos): RRHH y OPERADOR
  // entran a las órdenes como en el backend (TecnicaAuthChecker), y el pañolero ROLE_USER por su nivel de stock.
  // Quien sólo carga reclamos (alcance «solo solicitudes») ve el ítem y cae en Solicitudes.
  const verTecnica = !!rutaInicialTecnica(permisosTecnica({ rol: role, modulos: modules, isTecnicaAuthorized, tecnicaStockNivel, tecnicaScope }));

  const navigate = useNavigate();
  const url = 'https://api.softnik.tech/upload';
  const Swal = require('sweetalert2');

  const userName = selectedEmployee
    ? `${selectedEmployee.person?.name || ""} ${selectedEmployee.person?.lastname || ""}`.trim()
    : "";
  const userRoleLabel = ROLE_LABELS[getDisplayRol] || ROLE_LABELS[role] || "";
  const userInitials = selectedEmployee
    ? `${selectedEmployee.person?.name?.[0] || ""}${selectedEmployee.person?.lastname?.[0] || ""}`.toUpperCase()
    : "";

  const [pendingRequests, setPendingRequests] = useState(0);
  const [pendingTickets, setPendingCount] = useState(0);
  const [pendingRecibos, setPendingRecibos] = useState(0);
  const [pendingCapacitaciones, setPendingCapacitaciones] = useState(0);

  const refreshPendingSummary = useCallback(async () => {
    if (!employeeUuid) return;
    try {
      const res = await getPendingMe();
      setPendingRequests(res?.data?.requests?.total ?? 0);
      setPendingRecibos(hasModule('SUELDOS') ? (res?.data?.receipts?.total ?? 0) : 0);
      setPendingCapacitaciones(hasModule('CAPACITACIONES') ? (res?.data?.courses?.total ?? 0) : 0);
      setPendingCount(hasModule('HELPDESK') ? (res?.data?.tickets?.total ?? 0) : 0);
    } catch (e) {
      console.error("Error refreshPendingSummary:", e);
      setPendingRequests(0);
      setPendingRecibos(0);
      setPendingCapacitaciones(0);
      setPendingCount(0);
    }
  }, [employeeUuid, modules]);

  // Sincronizar con pendingSummary del contexto (carga inicial, sin llamada extra)
  useEffect(() => {
    if (!pendingSummary) return;
    setPendingRequests(pendingSummary?.requests?.total ?? 0);
    setPendingRecibos(hasModule('SUELDOS') ? (pendingSummary?.receipts?.total ?? 0) : 0);
    setPendingCapacitaciones(hasModule('CAPACITACIONES') ? (pendingSummary?.courses?.total ?? 0) : 0);
    setPendingCount(hasModule('HELPDESK') ? (pendingSummary?.tickets?.total ?? 0) : 0);
  }, [pendingSummary, modules]);

  // Listeners para refrescar cuando el usuario interactúa (firma recibo, etc.)
  useEffect(() => {
    if (!isSessionReady) return;

    window.addEventListener("updatePendingRequests", refreshPendingSummary);
    window.addEventListener("updatePendingRecibos", refreshPendingSummary);
    window.addEventListener("updatePendingCapacitaciones", refreshPendingSummary);
    window.addEventListener("updatePendingTickets", refreshPendingSummary);
    window.addEventListener("confirmarFirma", refreshPendingSummary);
    return () => {
      window.removeEventListener("updatePendingRequests", refreshPendingSummary);
      window.removeEventListener("updatePendingRecibos", refreshPendingSummary);
      window.removeEventListener("updatePendingCapacitaciones", refreshPendingSummary);
      window.removeEventListener("updatePendingTickets", refreshPendingSummary);
      window.removeEventListener("confirmarFirma", refreshPendingSummary);
    };
  }, [refreshPendingSummary, isSessionReady]);

  const importarExcel = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const requestOptions = {
        method: 'POST',
        data: formData,
        url: url,
        headers: {
          Authorization: `Bearer ${typeof getToken === 'function' ? getToken() : getToken}`
        },
      };

      const response = await axios(requestOptions);
      if (response.status === 200 && response.data.success) {
        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: 'Excel agregado con éxito.',
          showConfirmButton: false,
          timer: 1500
        });
      } else {
        console.error('Hubo un problema al importar el excel.');
      }
    } catch (error) {
      console.error('Error al importar el excel:', error?.response?.data?.message || error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.message || 'Error desconocido',
        footer: 'Formato válido: .xlsx'
      });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (file) importarExcel(file);
  };

  const logOut = () => {
    setStates();
    removeUserSession();
  };
  const handleLogout = () => {
    logOut();
    navigate("/");
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1081) setFlag(!flag);
  };

  return (
    <div className={`sidebar ${!flag && 'open'}`}>
      <div className="sidebar-collapse-btn" onClick={() => setFlag(!flag)}>
        {flag ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
      </div>
      <div className='contenedor-navbar-items'>
        {/* Header de perfil (solo mobile) */}
        <div className="sidebar-profile-header">
          <div className="sidebar-profile-avatar" onClick={() => { handleNavClick(); navigate("/miperfil"); }}>
            {profileImage
              ? <img src={profileImage} alt="perfil" />
              : <span>{userInitials}</span>
            }
          </div>
          <span className="sidebar-profile-name">{userName}</span>
          <span className="sidebar-profile-role">{userRoleLabel}</span>
        </div>

        <div className={`${flag ? 'list-menu' : 'list-menu-close'}`}>

          {/* ===== PRINCIPAL ===== */}
          <div className={`nav-section-title ${!flag && 'inactive'}`}>Principal</div>

          <NavLink to="/home/" className='item-navbar' onClick={handleNavClick}>
            <House size={18} className="icon-flag" />
            <p className={`${!flag && 'inactive'}`}>Panel</p>
          </NavLink>

          {hasRole('ROLE_ADMIN', 'ROLE_RRHH', 'ROLE_JEFE_AREA', 'ROLE_SUPERVISOR', 'ROLE_OPERADOR') && (
            <NavLink to="/RRHH" className='item-navbar' onClick={handleNavClick}>
              <UsersRound size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>
                RRHH
                {pendingRequests > 0 && (
                  <span className="pending-badge" title="Solicitudes pendientes">
                    {pendingRequests}
                  </span>
                )}
              </p>
            </NavLink>
          )}

          {hasModule('EMPRESAS') && hasRole('ROLE_ADMIN', 'ROLE_RRHH', 'ROLE_JEFE_AREA', 'ROLE_SUPERVISOR', 'ROLE_OPERADOR') && (
            <NavLink to="/objetivos/" className='item-navbar' onClick={handleNavClick}>
              <Building2 size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Empresas</p>
            </NavLink>
          )}

          {/* ===== OPERACIÓN ===== */}
          {((!isBasicRole && (hasModule('PRESENTISMO') || ((hasModule('SUPERVISION') || hasModule('RONDAS') || hasModule('SIMULACROS') || hasModule('ANALISIS_RIESGO')) && hasRole('ROLE_ADMIN', 'ROLE_JEFE_AREA', 'ROLE_SUPERVISOR')) || (hasModule('FLOTAS') && (hasRole('ROLE_ADMIN') || isFleetAuthorized)) || (hasModule('ARMAMENTOS') && (hasRole('ROLE_ADMIN') || isArmamentAuthorized || isDeliveryAuthorized)) || (hasModule('MEDICINA') && (hasRole('ROLE_ADMIN') || isMedLabAuthorized)))) || verTecnica) && (
            <div className={`nav-section-title ${!flag && 'inactive'}`}>Operación</div>
          )}

          {hasModule('PRESENTISMO') && !isBasicRole && (
            <NavLink to="/Presentismo" className='item-navbar' onClick={handleNavClick}>
              <UserCheck size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Presentismo</p>
            </NavLink>
          )}

          {(hasModule('SUPERVISION') || hasModule('RONDAS') || hasModule('SIMULACROS') || hasModule('ANALISIS_RIESGO')) && hasRole('ROLE_ADMIN', 'ROLE_JEFE_AREA', 'ROLE_SUPERVISOR') && (
            <NavLink to="/admin/supervision" className='item-navbar' onClick={handleNavClick}>
              <ClipboardCheck size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Supervisión</p>
            </NavLink>
          )}

          {hasModule('FLOTAS') && (hasRole('ROLE_ADMIN') || isFleetAuthorized) && (
            <NavLink to="/admin/flota" className='item-navbar' onClick={handleNavClick}>
              <Truck size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Flota</p>
            </NavLink>
          )}

          {hasModule('ARMAMENTOS') && (hasRole('ROLE_ADMIN') || isArmamentAuthorized) && (
            <NavLink to="/admin/armamentos" className='item-navbar' onClick={handleNavClick}>
              <Crosshair size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Armamentos</p>
            </NavLink>
          )}

          {verTecnica && (
            <NavLink to="/admin/tecnica" className='item-navbar' onClick={handleNavClick}>
              <Wrench size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Técnica</p>
            </NavLink>
          )}

          {hasModule('ARMAMENTOS') && !hasRole('ROLE_ADMIN') && !isArmamentAuthorized && isDeliveryAuthorized && (
            <NavLink to="/admin/armamentos/entregas" className='item-navbar' onClick={handleNavClick}>
              <ArrowLeftRight size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Entregas Armamento</p>
            </NavLink>
          )}

          {hasModule('MEDICINA') && (hasRole('ROLE_ADMIN') || isMedLabAuthorized) && (
            <NavLink to="/admin/medicina-laboral" className='item-navbar' onClick={handleNavClick}>
              <HeartPulse size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Medicina Laboral</p>
            </NavLink>
          )}

          {/* ===== GESTIÓN ===== */}
          {!isBasicRole && (hasModule('HELPDESK') || (hasModule('AUDITORIA') && (hasRole('ROLE_ADMIN') || hasAuditorChecklist)) || (hasModule('UNIFORMES') && isUniformAuthorized && !hasRole('ROLE_ADMIN', 'ROLE_RRHH', 'ROLE_JEFE_AREA', 'ROLE_SUPERVISOR')) || ((hasModule('CAPACITACIONES') || hasModule('CALIDAD')) && (hasRole('ROLE_ADMIN', 'ROLE_JEFE_AREA') || isCapAuthorized || isCalidadAuthorized))) && (
            <div className={`nav-section-title ${!flag && 'inactive'}`}>Gestión</div>
          )}

          {((hasModule('CAPACITACIONES') && (hasRole('ROLE_ADMIN', 'ROLE_JEFE_AREA') || isCapAuthorized)) || (hasModule('CALIDAD') && (hasRole('ROLE_ADMIN') || isCalidadAuthorized))) && (
            <NavLink to="/SeguridadHigiene" className='item-navbar' onClick={handleNavClick}>
              <ShieldHalf size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>SGI</p>
            </NavLink>
          )}

          {hasModule('AUDITORIA') && (hasRole('ROLE_ADMIN') || hasAuditorChecklist) && (
            <NavLink to="/admin/auditor-checklist" className='item-navbar' onClick={handleNavClick}>
              <ClipboardCheck size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Auditorias</p>
            </NavLink>
          )}

          {hasModule('HELPDESK') && !isBasicRole && (
            <NavLink to={`/tickets`} className='item-navbar' onClick={handleNavClick}>
              <LifeBuoy size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>
                Tickets
                {pendingTickets > 0 && (
                  <span className="pending-badge" title="Tickets con actividad pendiente">
                    {pendingTickets}
                  </span>
                )}
              </p>
            </NavLink>
          )}

          {hasModule('UNIFORMES') && isUniformAuthorized && !hasRole('ROLE_ADMIN', 'ROLE_RRHH', 'ROLE_JEFE_AREA', 'ROLE_SUPERVISOR') && (
            <NavLink to="/admin/uniformes" className='item-navbar' onClick={handleNavClick}>
              <Shirt size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Uniformes</p>
            </NavLink>
          )}

          {/* ===== ADMINISTRACIÓN ===== */}
          {((hasModule('SUELDOS') && (hasRole('ROLE_ADMIN') || hasRole('ROLE_RRHH'))) || (hasModule('FACTURACION') && hasRole('ROLE_ADMIN'))) && (
            <div className={`nav-section-title ${!flag && 'inactive'}`}>Administración</div>
          )}

          {hasModule('SUELDOS') && (hasRole('ROLE_ADMIN') || hasRole('ROLE_RRHH')) && (
            <NavLink to={sueldosPath} className='item-navbar' onClick={handleNavClick}>
              <Calculator size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Sueldos</p>
            </NavLink>
          )}

          {hasModule('FACTURACION') && hasRole('ROLE_ADMIN') && (
            <NavLink to="/portal-facturacion/dashboard" className='item-navbar' onClick={handleNavClick}>
              <Receipt size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Facturación</p>
            </NavLink>
          )}

          {/* ===== OPERACIÓN (para ROLE_USER/ROLE_TECNICO autorizado) ===== */}
          {isBasicRole && isCapAuthorized && hasModule('CAPACITACIONES') && (
            <>
              {/* Si ve Técnica, el título «Operación» ya salió arriba: no se repite. */}
              {!verTecnica && <div className={`nav-section-title ${!flag && 'inactive'}`}>Operación</div>}
              <NavLink to="/SeguridadHigiene" className='item-navbar' onClick={handleNavClick}>
                <ShieldHalf size={18} className='icon-flag' />
                <p className={`${!flag && 'inactive'}`}>SGI</p>
              </NavLink>
            </>
          )}

          {/* ===== GESTIÓN (usuario) ===== */}
          {isBasicRole && (
            <>
              <div className={`nav-section-title ${!flag && 'inactive'}`}>Gestión</div>

              <NavLink to="/solicitudes" className='item-navbar' onClick={handleNavClick}>
                <MailOpen size={18} className='icon-flag' />
                <p className={`${!flag && 'inactive'}`}>
                  Solicitudes
                  {pendingRequests > 0 && (
                    <span className="pending-badge" title="Solicitudes pendientes">
                      {pendingRequests}
                    </span>
                  )}
                </p>
              </NavLink>

              <NavLink to={`/suspensiones/${employeeUuid}`} className='item-navbar' onClick={handleNavClick}>
                <Gavel size={18} className='icon-flag' />
                <p className={`${!flag && 'inactive'}`}>Sanciones</p>
              </NavLink>

              {hasModule('EVALUACIONES') && (
                <NavLink to="/mis-evaluaciones" className='item-navbar' onClick={handleNavClick}>
                  <ClipboardCheck size={18} className='icon-flag' />
                  <p className={`${!flag && 'inactive'}`}>Evaluación de Desempeño</p>
                </NavLink>
              )}
            </>
          )}

          {/* ===== FORMACIÓN ===== */}
          {(
            (hasModule('CAPACITACIONES') && !isCapAuthorized && (hasRole('ROLE_SUPERVISOR', 'ROLE_OPERADOR') || isBasicRole)) ||
            (hasModule('HABILITACIONES') && isHabAuthorized && isBasicRole)
          ) && (
            <div className={`nav-section-title ${!flag && 'inactive'}`}>Formación</div>
          )}

          {hasModule('CAPACITACIONES') && !isCapAuthorized && (hasRole('ROLE_SUPERVISOR', 'ROLE_OPERADOR') || isBasicRole) && (
            <NavLink to="capacitaciones/" className='item-navbar' onClick={handleNavClick}>
              <GraduationCap size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>
                Capacitaciones
                {pendingCapacitaciones > 0 && (
                  <span className="pending-badge" title="Capacitaciones pendientes">
                    {pendingCapacitaciones}
                  </span>
                )}
              </p>
            </NavLink>
          )}

          {isBasicRole && !isCapAuthorized && (
            <NavLink to="/biblioteca-normas" className='item-navbar' onClick={handleNavClick}>
              <BookOpen size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Biblioteca</p>
            </NavLink>
          )}

          {hasModule('HABILITACIONES') && isHabAuthorized && isBasicRole && (
            <NavLink to="/admin/habilitaciones-personal" className='item-navbar' onClick={handleNavClick}>
              <ShieldHalf size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Habilitaciones</p>
            </NavLink>
          )}

          {hasModule('EVALUACIONES') && hasRole('ROLE_OPERADOR') && (
            <NavLink to="/mis-evaluaciones" className='item-navbar' onClick={handleNavClick}>
              <ClipboardCheck size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Evaluación de Desempeño</p>
            </NavLink>
          )}

          {/* ===== CUMPLIMIENTO (JEFE_AREA / SUPERVISOR) ===== */}
          {hasModule('HABILITACIONES') && isHabAuthorized && hasRole('ROLE_SUPERVISOR', 'ROLE_JEFE_AREA') && (
            <>
              <div className={`nav-section-title ${!flag && 'inactive'}`}>Cumplimiento</div>
              <NavLink to="/admin/habilitaciones-personal" className='item-navbar' onClick={handleNavClick}>
                <ShieldHalf size={18} className='icon-flag' />
                <p className={`${!flag && 'inactive'}`}>Habilitaciones</p>
              </NavLink>
            </>
          )}

          {/* ===== PERSONAL ===== */}
          <div className={`nav-section-title ${!flag && 'inactive'}`}>Personal</div>

          {hasRole('ROLE_SUPERVISOR', 'ROLE_OPERADOR') && !isCapAuthorized && (
            <NavLink to="/biblioteca-normas" className='item-navbar' onClick={handleNavClick}>
              <BookOpen size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Biblioteca</p>
            </NavLink>
          )}

          {hasModule('FLOTAS') && hasAssignedVehicle && !hasRole('ROLE_ADMIN') && !isFleetAuthorized && (
            <NavLink to="/mi-vehiculo" className='item-navbar' onClick={handleNavClick}>
              <Car size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>Mi Vehículo</p>
            </NavLink>
          )}

          {hasModule('SUELDOS') && (
            <NavLink to={`/mis-recibos/${employeeUuid}`} className='item-navbar' onClick={handleNavClick}>
              <Receipt size={18} className='icon-flag' />
              <p className={`${!flag && 'inactive'}`}>
                Recibos
                {pendingRecibos > 0 && (
                  <span className="pending-badge" title="Recibos pendientes por firmar">
                    {pendingRecibos}
                  </span>
                )}
              </p>
            </NavLink>
          )}

          <NavLink to={`/miperfil`} className='item-navbar' onClick={handleNavClick}>
            <User size={18} className='icon-flag' />
            <p className={`${!flag && 'inactive'}`}>Mi perfil</p>
          </NavLink>

          {/* ===== AYUDA ===== */}
          {!isBasicRole && (
            <>
              <div className={`nav-section-title ${!flag && 'inactive'}`}>Ayuda</div>
              <a href="https://drive.google.com/drive/folders/14taOZZPHeEev-PdbtNEkYMnGdgKCxoqo?usp=sharing" target="_blank" rel="noopener noreferrer" className='item-navbar' onClick={handleNavClick}>
                <BookIcon size={18} className='icon-flag' />
                <p className={`${!flag && 'inactive'}`}>Manual de Usuario</p>
              </a>
            </>
          )}
        </div>

        <div className="sidebar-footer">
          <LogOut size={18} className={`icon-flag-session open`} onClick={handleLogout} />
          <button className={`logout-button ${!flag && 'inactive'}`} onClick={handleLogout} href="/">Cerrar Sesión</button>
        </div>
      </div>
    </div>
  );
}

export default Navbar;